/**
 * Error copy for free-tool forms that serve both English and Hindi pages.
 *
 * A tool's own input checks throw ToolInputError with copy already written in
 * the page language. An API 4xx carries a `detail` written for the seeker (a
 * field validation message), so English pages still show it. Everything else
 * is transport or server text written for developers — "Request timed out
 * after 12000ms: https://…", "Request unreachable after 3 attempts (HTTP 500)",
 * "HTTP 500" — and maps to localized copy instead, on every page.
 *
 * Errors are read by `name`/`status` rather than `instanceof` so this module
 * stays free of the API client graph (calculator islands import it).
 */
export class ToolInputError extends Error {}

export interface ToolErrorCopy {
  /** Generic line for anything not classified below. */
  fallback: string;
  /** The request ran past its client timeout. */
  timeout?: string;
  /** The browser could not reach the server (offline, DNS, CORS, reset). */
  network?: string;
  /** The server answered 5xx (engine down, provider outage). */
  server?: string;
  /**
   * Copy per API error `code` (ApiError.code, from the backend's
   * `{detail, code}`). A mapped code wins over the 4xx detail on every page,
   * so its English detail never reaches a seeker; an unmapped code keeps the
   * rules below.
   */
  codes?: Readonly<Record<string, string>>;
}

type FailureKind = "timeout" | "network" | "server" | "client" | "unknown";

function errorName(err: unknown): string {
  if (typeof err !== "object" || err === null) return "";
  const name = (err as { name?: unknown }).name;
  return typeof name === "string" ? name : "";
}

/**
 * 408 and 429 are the engine saying it is busy (jyotish-api answers 429 when
 * Cloud Run has no free instance), not a fault in what the seeker typed, so
 * they read as the server line.
 */
const BUSY_STATUSES = new Set([408, 429]);

function apiFailureKind(err: unknown): FailureKind {
  const status = (err as { status?: unknown }).status;
  if (typeof status !== "number" || status < 400) return "unknown";
  return status >= 500 || BUSY_STATUSES.has(status) ? "server" : "client";
}

function exhaustedFailureKind(err: unknown): FailureKind {
  const cause = String((err as { lastCause?: unknown }).lastCause ?? "");
  if (/\b(?:HTTP\s*)?5\d\d\b/.test(cause) || /\bHTTP\s*(?:408|429)\b/.test(cause)) {
    return "server";
  }
  return /timeout|timed out/i.test(cause) ? "timeout" : "network";
}

function classifyFailure(err: unknown): FailureKind {
  const name = errorName(err);
  if (name === "TimeoutError" || name === "AbortError") return "timeout";
  if (err instanceof TypeError) return "network";
  if (name === "ApiError") return apiFailureKind(err);
  if (name === "RetryExhaustedError") return exhaustedFailureKind(err);
  return "unknown";
}

/**
 * A 4xx detail written for the seeker. The API client falls back to the bare
 * "HTTP <status>" when the body carries no FastAPI detail; that is developer
 * text, so it never counts.
 */
function clientDetail(err: unknown, language: "en" | "hi"): string {
  if (language !== "en" || !(err instanceof Error)) return "";
  const detail = err.message.trim();
  return /^HTTP \d{3}$/.test(detail) ? "" : detail;
}

function codedMessage(err: unknown, copy: ToolErrorCopy): string | undefined {
  if (!copy.codes || errorName(err) !== "ApiError") return undefined;
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" ? copy.codes[code] : undefined;
}

export function toolErrorMessage(
  err: unknown,
  language: "en" | "hi",
  fallbackOrCopy: string | ToolErrorCopy
): string {
  const copy: ToolErrorCopy =
    typeof fallbackOrCopy === "string" ? { fallback: fallbackOrCopy } : fallbackOrCopy;
  if (err instanceof ToolInputError) return err.message;
  const coded = codedMessage(err, copy);
  if (coded) return coded;
  const kind = classifyFailure(err);
  if (kind === "client") return clientDetail(err, language) || copy.fallback;
  if (kind === "unknown") return copy.fallback;
  return copy[kind] ?? copy.fallback;
}
